import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import z from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { PinnedEscalationNote } from '@/features/luxe-submissions/components/pinned-escalation-note'
import { ScoreFormGallery } from '@/features/luxe-submissions/components/score-form-gallery'
import { ScoreFormHeader } from '@/features/luxe-submissions/components/score-form-header'
import { ScoreFormListingDetails } from '@/features/luxe-submissions/components/score-form-listing-details'
import { ScoreFormLoading } from '@/features/luxe-submissions/components/score-form-loading'
import { ScoreFormPanel } from '@/features/luxe-submissions/components/score-form-panel'
import { useGetVisualVettingListingDetails } from '@/features/luxe-submissions/queries/use-get-visual-vetting-listing-details'
import type { ScoreFormData } from '@/features/luxe-submissions/types'
import { getReviewerName } from '@/features/luxe-submissions/utils'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsListingScoreForm() {
	const { t } = useTranslation()
	const urlParams = useURLParsedParams(
		z.object({
			id: z.string()
		})
	)

	const listingId = urlParams.success
		? String(urlParams.data.id)
		: raise('Invalid listing ID in URL parameters')

	const { data, isLoading, isError } = useGetVisualVettingListingDetails({
		listingId
	})

	const methods = useForm<ScoreFormData>()

	useEffect(() => {
		if (data) {
			methods.reset({
				answers: Object.fromEntries(
					data.answers
						.filter((a) => a.answer != null)
						.map((a) => [a.questionId, a.answer as string])
				)
			})
		}
	}, [data, methods])

	if (isLoading) {
		return <ScoreFormLoading />
	}
	if (!data || isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	const isCompleted = data.vettingStepStatus === 'completed'
	const facilities = [
		{ keyName: 'guests', count: data.maxGuests },
		{ keyName: 'bedrooms', count: data.bedrooms },
		{ keyName: 'beds', count: data.beds },
		{ keyName: 'baths', count: data.bathrooms }
	].filter((item) => item.count != null) as {
		keyName: 'bedrooms' | 'beds' | 'baths' | 'guests'
		count: number
	}[]

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<FormProvider {...methods}>
				{/* Header */}
				<ScoreFormHeader listingData={data} />

				{/* Main content */}
				<div className='grid grid-cols-[1.5fr_2fr] overflow-hidden'>
					<ScrollArea
						className='h-full'
						scrollbar={<Scrollbar />}
						style={{ overflow: 'hidden' }}
					>
						<div className='border-neutral-200 border-r'>
							{isCompleted && (
								<PinnedEscalationNote
									blockersFailedCount={
										data.blockersFailedCount
									}
									designScore={data.score}
									reviewerName={
										data.vetter
											? getReviewerName(data.vetter)
													.fullName
											: null
									}
								/>
							)}
							<ScoreFormPanel
								formDisabled={
									!(
										data?.vetter &&
										data?.isAssignedToCurrentUser
									) ||
									data?.vettingStepStatus !== 'in_progress'
								}
								questions={data.questions}
								// Both pin to the top of the column, and a
								// completed form has nothing left to count
								showCounter={!isCompleted}
							/>
						</div>
					</ScrollArea>

					<div className='flex flex-col gap-8 overflow-auto px-6'>
						<ScoreFormGallery
							listingId={data.airbnbListingId ?? data.id}
							photos={data.images}
							repositoryUrl={data.photoRepositoryUrl}
						/>
						<ScoreFormListingDetails facilities={facilities} />
					</div>
				</div>
			</FormProvider>
		</div>
	)
}

export { LuxeSubmissionsListingScoreForm }
